import React, {useEffect} from "react";
import {Box, Text, View} from "@unibuddy/patron";
import {useBooleanFeatureFlag} from "ub-feature-flagging-react";
import {useApolloClient, useMutation} from "react-apollo";
import {useHistory, useLocation} from "react-router-dom";
import {useTranslation} from "@unibuddy/intl";

import {SSO_ENABLED} from "shared/constants";
import registerApplicantStage1 from "shared/Register/mutations/registerApplicantStage1";
import useAuth from "shared/Auth/hooks/useAuth";
import useAnalytics from "shared/AnalyticsNew/hooks/useAnalytics";
import {pageNames, sharedEvents} from "shared/AnalyticsNew/constants/productSpecification";
import Auth from "shared/Auth/auth";
import {rootURL} from "shared/Utils/WhereAmI";

import {getReduxDispatch} from "../../../../App";
import {GoogleAuthButton} from "./GoogleAuthButton";
import {RegisterPage1Form} from "./RegisterPage1Form";
import {triggerSSOPopup} from "../../SSO/SSOTrigger";
import {useUniversity} from "../../UniversityProvider/UniversityProvider";
import {AuthFormError} from "../../../../components/AuthFormError/AuthFormError";
import {getSSOErrorUrl, setSSOCtaUrl} from "../../SSO/utils";
import {UnibuddyPolicyText} from "./UnibuddyPolicyText";
import {IntlNamespaces} from "../../../../translations/types";
import {INTL_CONTINUE_WITH_EMAIL} from "../../../../translations/login";
import {INTL_SIGN_UP_GOOGLE, INTL_SIGN_UP_GOOGLE_ERROR} from "../../../../translations/signup";
import {
    ApplicantUnibuddySignupClientType,
    ApplicantUnibuddySignupSourceType,
} from "../../../../types/generated";

export function RegisterPage1Content({primaryApp}: {primaryApp: string}) {
    const {slug: universitySlug, id: universityId} = useUniversity();
    const {t} = useTranslation([IntlNamespaces.SIGNUP, IntlNamespaces.LOGIN]);
    const isSSOEnabled = useBooleanFeatureFlag(SSO_ENABLED.key, SSO_ENABLED.value);
    const [registerApplicant] = useMutation(registerApplicantStage1);
    const {authState} = useAuth();
    const {trackEvent, aliasVisitor} = useAnalytics();
    const client = useApolloClient();
    const dispatch = getReduxDispatch();
    const location = useLocation();
    const history = useHistory();
    const SSO_BUTTON_TEXT = "Sign up with Google";
    const hasSSOError = location.pathname === getSSOErrorUrl();

    const handleSubmit = async (
        firstName: string,
        lastName: string,
        email: string,
        password: string,
    ): Promise<void> => {
        try {
            const unibuddySignupMetadata = {
                sourceType: location.pathname?.includes("/external/")
                    ? ApplicantUnibuddySignupSourceType.ExternalBuddyCard
                    : ApplicantUnibuddySignupSourceType.UniversityWidget,
                clientType: location.pathname?.includes("/pwa/")
                    ? ApplicantUnibuddySignupClientType.Pwa
                    : ApplicantUnibuddySignupClientType.Embed,
                universityId,
                url: window.location.href,
            };
            const {data} = await registerApplicant({
                variables: {
                    firstName,
                    lastName,
                    email,
                    password,
                    universitySlug,
                    primaryApp,
                    // checkForAmbassador is legacy implementation and does nothing, setting false
                    checkForAmbassador: false,
                    ageVerificationInfo: authState?.me?.applicant?.ageVerificationInfo ?? null,
                    unibuddySignupMetadata,
                },
            });

            if (data.createApplicantV2.applicant) {
                trackEvent(sharedEvents.accountCreated.name, {
                    accountRole: "applicant",
                });
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                const applicant = await Auth.login(client, authState, dispatch, email, password);

                // Set analytics user
                aliasVisitor({
                    id: applicant.anyUser.id,
                    accountRole: applicant.anyUser.accountRole,
                });

                const pathname = location.pathname;
                const rootUrl = rootURL(pathname);
                const fallBackUrl = `${rootUrl}/buddies`;
                history.replace({
                    pathname: pathname.replace(/register$/, "register2"),
                    state: {nextPathname: location?.state?.nextPathname ?? fallBackUrl},
                    search: location.search,
                });
            }
        } catch (e) {
            console.error(e);
            throw e;
        }
    };

    useEffect(() => {
        if (isSSOEnabled) {
            trackEvent(sharedEvents.ssoButtonVisible.name, {
                universitySlug,
                page: pageNames.prospectSignUpStageOne,
            });
        }
    }, [isSSOEnabled, trackEvent, universitySlug]);

    return (
        <View gap="medium-large" minW="100%">
            {isSSOEnabled && (
                <View gap="medium-large">
                    <GoogleAuthButton
                        buttonText={t(
                            `${IntlNamespaces.SIGNUP}:${INTL_SIGN_UP_GOOGLE.key}`,
                            SSO_BUTTON_TEXT,
                        )}
                        onClick={() => {
                            trackEvent(sharedEvents.ssoButtonClicked.name, {
                                universitySlug,
                                cta: SSO_BUTTON_TEXT,
                                page: pageNames.prospectSignUpStageOne,
                            });
                            setSSOCtaUrl(location.pathname);
                            triggerSSOPopup(universitySlug);
                        }}
                    />
                    {hasSSOError && (
                        <AuthFormError>
                            {t(
                                `${IntlNamespaces.SIGNUP}:${INTL_SIGN_UP_GOOGLE_ERROR.key}`,
                                INTL_SIGN_UP_GOOGLE_ERROR.defaultValue,
                            )}
                        </AuthFormError>
                    )}
                    <Box display="flex" gap="small" alignItems="center">
                        <Box borderBottomWidth={1} borderColor="grey300" flexGrow={1} />
                        <Text color="grey600" transform="uppercase" size="xsmall">
                            {t(
                                `${IntlNamespaces.LOGIN}:${INTL_CONTINUE_WITH_EMAIL.key}`,
                                INTL_CONTINUE_WITH_EMAIL.defaultValue,
                            )}
                        </Text>
                        <Box borderBottomWidth={1} borderColor="grey300" flexGrow={1} />
                    </Box>
                </View>
            )}
            <RegisterPage1Form handleSubmit={handleSubmit} />
            <UnibuddyPolicyText />
        </View>
    );
}
